import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { teamName, teamColor, SLOT_LABEL, teamSpread, favoriteTeam } from "@/lib/teams";
import Countdown from "@/components/Countdown";
import { toast } from "sonner";
import { Clock, Lock, CheckCircle2, Loader2, ChevronDown, Scale, Layers } from "lucide-react";

function TeamButton({ abbr, line, selected, disabled, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`flex-1 flex flex-col items-start px-4 py-3 border transition-colors duration-200 ${
        selected
          ? "border-[#E31837] bg-[#E31837]/10"
          : "border-white/10 hover:border-white/30 bg-[#0A0A0A]"
      } ${disabled ? "cursor-not-allowed opacity-80" : "cursor-pointer"}`}
    >
      <div className="flex items-center gap-2 w-full">
        <span className="w-2 h-8" style={{ backgroundColor: teamColor(abbr) }} />
        <div className="text-left">
          <div className="text-lg font-['Bebas_Neue'] tracking-wide leading-none">{abbr}</div>
          <div className="text-[11px] text-white/50">{teamName(abbr)}</div>
        </div>
        {line != null && (
          <span className="ml-auto text-[11px] font-bold text-white/60 tabular-nums" data-testid={`${testid}-spread`}>{line}</span>
        )}
        {selected && <CheckCircle2 className="w-4 h-4 text-[#E31837] ml-1" />}
      </div>
    </button>
  );
}

function SpreadToggle({ game, team, taken, bonus, disabled, onChange }) {
  const fav = favoriteTeam(game);
  if (fav !== team) {
    return (
      <div className="px-3 pb-3 text-[10px] uppercase tracking-wider text-white/30" data-testid={`pick-${game.id}-underdog-note`}>
        {fav ? "Underdog · straight up only" : "No line posted · straight up only"}
      </div>
    );
  }
  const opt = (on, label, testid) => (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(on)}
      data-testid={testid}
      className={`flex-1 py-1.5 text-[11px] uppercase tracking-wider font-semibold border transition-colors duration-200 ${
        taken === on ? "border-[#EAB308] bg-[#EAB308]/15 text-[#EAB308]" : "border-white/10 text-white/50 hover:border-white/30"
      } ${disabled ? "cursor-not-allowed" : ""}`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex gap-2 px-3 pb-3" data-testid={`pick-${game.id}-spread-toggle`}>
      {opt(false, "Straight Up", `pick-${game.id}-su`)}
      {opt(true, `Cover ${teamSpread(game, team)} · +${bonus}`, `pick-${game.id}-spread`)}
    </div>
  );
}

const PT_COLORS = { 1.5: "text-white/60", 2.5: "text-[#EAB308]", 5.0: "text-[#E31837]" };

export default function Picks() {
  const [weeks, setWeeks] = useState([]);
  const [weekNum, setWeekNum] = useState(null);
  const [week, setWeek] = useState(null);
  const [selections, setSelections] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({ pick_mode: "straight_up", cover_bonus: 1 });
  const [spreadPicks, setSpreadPicks] = useState([]);
  const mode = settings.pick_mode;
  const ats = mode === "ats";
  const combo = mode === "combo";
  const showLines = ats || combo;

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/weeks").then((res) => {
      setWeeks(res.data);
      const open = res.data.find((w) => !w.locked);
      const def = open ? open.week_number : res.data[res.data.length - 1]?.week_number;
      setWeekNum(def);
    });
  }, []);

  useEffect(() => {
    if (!weekNum) return;
    setLoading(true);
    Promise.all([api.get(`/weeks/${weekNum}`), api.get(`/picks/${weekNum}/me`)])
      .then(([w, p]) => {
        setWeek(w.data);
        setSelections(p.data.selections || {});
        setSpreadPicks(p.data.spread_picks || []);
      })
      .finally(() => setLoading(false));
  }, [weekNum]);

  const pick = (gameId, team) => {
    if (week?.locked) return;
    setSelections((s) => ({ ...s, [gameId]: team }));
    setSpreadPicks((sp) => sp.filter((id) => id !== gameId));
  };

  const setSpread = (gameId, on) => {
    if (week?.locked) return;
    setSpreadPicks((sp) => (on ? [...new Set([...sp, gameId])] : sp.filter((id) => id !== gameId)));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/picks/${weekNum}`, { selections, spread_picks: spreadPicks });
      toast.success("Picks saved!", { description: `Week ${weekNum} locked in.` });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const madeCount = week ? week.games.filter((g) => selections[g.id]).length : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-['Bebas_Neue'] text-5xl tracking-tight leading-none">MAKE YOUR PICKS</h1>
          <p className="text-white/50 text-sm mt-1">{ats ? "Pick the team you think will cover the spread in every matchup." : combo ? "Pick a winner in every matchup — or take the favorite with the spread for a cover bonus." : "Choose a winner for every matchup before the cutoff."}</p>
        </div>
        <div className="relative">
          <select
            value={weekNum || ""}
            onChange={(e) => setWeekNum(Number(e.target.value))}
            className="appearance-none bg-[#141414] border border-white/10 pl-4 pr-10 py-2.5 rounded-sm text-sm uppercase tracking-wider focus:ring-2 focus:ring-[#E31837] focus:outline-none"
            data-testid="week-selector"
          >
            {weeks.map((w) => (
              <option key={w.week_number} value={w.week_number}>
                Week {w.week_number} {w.locked ? "(Locked)" : "(Open)"}
              </option>
            ))}
          </select>
          <ChevronDown className="w-4 h-4 absolute right-3 top-3 pointer-events-none text-white/50" />
        </div>
      </div>

      {loading || !week ? (
        <div className="flex items-center gap-2 text-white/50 py-20 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading matchups…
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-4 bg-[#141414] border border-white/10 px-5 py-4 mb-6">
            <div className="flex items-center gap-2">
              {week.locked ? <Lock className="w-4 h-4 text-[#EF4444]" /> : <Clock className="w-4 h-4 text-[#EAB308]" />}
              <span className="text-xs uppercase tracking-[0.15em] text-white/50">Cutoff</span>
            </div>
            <div className="text-sm">
              {new Date(week.cutoff).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              {" · "}
              <Countdown target={week.cutoff} />
            </div>
            <div className="ml-auto text-sm text-white/60" data-testid="picks-progress">
              {madeCount}/{week.games.length} picked
            </div>
          </div>

          {week.locked && (
            <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 px-5 py-3 mb-6 text-sm flex items-center gap-2" data-testid="locked-banner">
              <Lock className="w-4 h-4 text-[#EF4444]" />
              Picks are locked for Week {week.week_number}. Head to Results to see how you did.
            </div>
          )}

          {ats && (
            <div className="bg-[#EAB308]/10 border border-[#EAB308]/30 px-5 py-3 mb-6 text-sm flex items-center gap-2" data-testid="ats-banner">
              <Scale className="w-4 h-4 text-[#EAB308]" />
              <span><span className="font-semibold text-[#EAB308]">Against the Spread</span> — the favorite must win by more than the number; the underdog covers by staying within it. Lines lock at cutoff.</span>
            </div>
          )}

          {combo && (
            <div className="bg-[#EAB308]/10 border border-[#EAB308]/30 px-5 py-3 mb-6 text-sm flex items-center gap-2" data-testid="combo-banner">
              <Layers className="w-4 h-4 text-[#EAB308]" />
              <span><span className="font-semibold text-[#EAB308]">Straight Up + Spread</span> — pick any team straight up, or take the <span className="font-semibold">favorite with the spread</span>: cover and earn the slot points <span className="font-semibold text-[#EAB308]">+{settings.cover_bonus} bonus</span>; fail to cover and it counts as a wrong pick. A push grades straight up.</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {week.games.map((g) => (
              <div key={g.id} className="bg-[#141414] border border-white/10" data-testid={`matchup-${g.id}`}>
                <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{SLOT_LABEL[g.slot]}</span>
                  <span className={`text-xs font-bold ${PT_COLORS[g.points] || "text-white/60"}`}>
                    {g.points} PT
                  </span>
                </div>
                <div className="flex gap-2 p-3">
                  <TeamButton
                    abbr={g.away}
                    line={showLines ? teamSpread(g, g.away) : null}
                    selected={selections[g.id] === g.away}
                    disabled={week.locked}
                    onClick={() => pick(g.id, g.away)}
                    testid={`pick-${g.id}-away`}
                  />
                  <div className="flex items-center text-white/30 text-xs font-bold">@</div>
                  <TeamButton
                    abbr={g.home}
                    line={showLines ? teamSpread(g, g.home) : null}
                    selected={selections[g.id] === g.home}
                    disabled={week.locked}
                    onClick={() => pick(g.id, g.home)}
                    testid={`pick-${g.id}-home`}
                  />
                </div>
                {combo && selections[g.id] && (
                  <SpreadToggle
                    game={g}
                    team={selections[g.id]}
                    taken={spreadPicks.includes(g.id)}
                    bonus={settings.cover_bonus}
                    disabled={week.locked}
                    onChange={(on) => setSpread(g.id, on)}
                  />
                )}
              </div>
            ))}
          </div>

          {!week.locked && (
            <div className="sticky bottom-4 mt-6 flex justify-end">
              <button
                onClick={save}
                disabled={saving}
                className="bg-[#E31837] hover:bg-[#C8102E] px-8 py-3 rounded-sm uppercase tracking-[0.2em] text-sm font-semibold transition-colors duration-200 flex items-center gap-2 shadow-lg shadow-black/50 disabled:opacity-60"
                data-testid="submit-picks-button"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Save Picks ({madeCount}/{week.games.length})
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
